import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Footer, Header, Meta, Breadcrumb } from '$partials/global';
import Image from '$partials/media/image';
import ModuleDispatcher from '$partials/modules/dispatcher';
import { useLocale } from '$contexts/locale';
import { getAllModules } from '$utilities/data';

import ModuleRelatedCarousel from '$partials/modules/individual/related-carousel';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const CaseStudyPage = ({ data, domain }) => {
    const { translate: t } = useLocale();

    const [isOpen, setIsOpen] = useState(false);

    const page = data?.page;

    const modules = getAllModules(page);

    if (!page) {return null;}

    return (
        <>
            <Meta page={page} />

            <main id='main-content'>
                <div className='min-h-screen'>

                    <Header
                        isOpen={isOpen}
                        menu={data?.navigation}
                        setIsOpen={setIsOpen}/>

                    <div className='layout-container z-10 relative'>

                        {/* Header */}
                        <div className='mt-[144px] md:mt-200'>
                            <div className='px-10 sm:px-5 md:px-10'>
                                {page.categories?.items?.length > 0 && (
                                    <div className='flex items-center mb-20 md:mb-30'>
                                        {page.categories.items[0]?.icon && (
                                            <div
                                                className='mr-10'
                                                style={{
                                                    fontSize: '18px',
                                                    width: `${(page.categories.items[0]?.icon.width / page.categories.items[0]?.icon.height)}em` ,
                                                    maxWidth: '100%'
                                                }}>
                                                <Image
                                                    {...page.categories.items[0]?.icon}
                                                    loading={''}/>
                                            </div>
                                        )}
                                        <p className='text-orange type-style-7'>{page.categories.items[0]?.title}</p>
                                    </div>
                                )}

                                <h1 className='type-style-1'>{page?.title}</h1>

                                {page.logo && (
                                    <div className='w-[124px] h-[124px] bottom-0 relative z-10 -mb-[62px] mt-30 md:mt-50'>
                                        <div className='bg-white rounded-full h-full w-full px-10'>
                                            <div
                                                className='bg-contain bg-center	bg-no-repeat rounded-full w-full h-full'
                                                style={{backgroundImage: `url(${page.logo?.src})`}}
                                                title={page.logo?.title} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {page.heroImage && page.displayHeroImageWhole && (
                        <figure className='max-w-full'>
                            <div className='aspect-[5/2]'>
                                <div className='h-full left-1/2 -translate-x-1/2 relative w-full px-10 '>
                                    <Image
                                        {...page.heroImage}
                                        className='w-full h-full object-cover'
                                        containerClassName='w-full h-full relative'
                                        height='100%'
                                        loadWidth={2560}
                                        width='100%' />
                                </div>
                            </div>
                        </figure>
                    )}

                    {page.heroImage && !page.displayHeroImageWhole && (
                        <figure className='max-w-full'>
                            <div className='left-1/2 -translate-x-1/2 relative w-full px-10 '>
                                <div
                                    className='min-h-[200px] sm:min-h-[422px] md:min-h-[475px] w-full bg-contain bg-repeat-x'
                                    style={{backgroundImage: `url(${page.heroImage?.src})`}}
                                    title={page.heroImage?.title} />
                            </div>
                        </figure>
                    )}

                    <div className='pt-55'>
                        <Breadcrumb
                            parent='Case Studies'
                            parentSlug='case-studies'
                            title={page?.title} />
                    </div>
                    {/* Goal Mobile / Tablet */}
                    {page.goal && (
                        <div className='md:hidden py-30 bg-sand mt-40 sm:mt-30'>
                            <div className='layout-container flex flex-wrap sm:justify-center'>
                                <div className='w-full sm:w-8/12 flex flex-col px-10 sm:px-5 md:px-10'>
                                    <p className='type-style-3 text-orange'>
                                        {t('The Goal')}
                                    </p>
                                </div>
                                <div
                                    className='w-full sm:w-8/12 flex flex-col px-10 sm:px-5 md:px-10 mt-25 items-center type-style-8'>
                                    {page.goal}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className='layout-container'>

                        {/* Goal Desktop */}
                        {page.goal && (
                            <div className='hidden md:grid grid-cols-12 flex-wrap rounded-button py-50 bg-sand border border-border-grey mx-10 mt-60 gap-x-20'>
                                <div className='col-span-4 flex flex-col pl-50'>
                                    <p className='type-style-3 text-orange'>
                                        {t('The Goal')}
                                    </p>
                                </div>
                                <div
                                    className='col-span-8 flex flex-col pr-50 type-style-9'>
                                    {page.goal}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Content */}
                    {(modules?.length > 0) && (
                        <section className='mt-40 sm:mt-50 md:mt-60'>
                            {modules.map((module, i) => {
                                return (
                                    <ModuleDispatcher
                                        key={i}
                                        module={module}
                                        nextTypename={modules[i + 1]?.__typename}
                                        parent={'Detail'}
                                        previousTypename={modules[i - 1]?.__typename} />
                                );
                            })}
                        </section>
                    )}

                    {/* Related */}
                    {data.relatedArticles?.items?.length > 0 && (
                        <ModuleRelatedCarousel
                            ctaLabel={t('View All')}
                            ctaLink='/case-studies'
                            items={data.relatedArticles?.items}
                            parent={'Detail'}
                            title={'Related Case Studies'}/>
                    )}
                    <div className='layout-container'>
                        <div className='pt-60 md:pt-20'>
                            <Footer
                                domain={domain}
                                menu={data?.footer} />
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

/* =========================================================================  */
/* Typing and exports */
/* =========================================================================  */

const propTypes = {
    data: PropTypes.shape({
        footer: PropTypes.shape({
            items: PropTypes.array
        }),
        page: PropTypes.object,
        relatedArticles: PropTypes.object,
        navigation: PropTypes.object,
    }),
    domain: PropTypes.string,
};

CaseStudyPage.propTypes = propTypes;

export default CaseStudyPage;
